import { WorkspaceType } from '@gripp/shared-logic';
import {
  Colors,
  ControlledPhoneInput,
  ControlledSelect,
  ControlledTextInput,
  PhoneValidator,
  Spacing,
  tokens,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Checkbox } from 'react-native-paper';
import { boolean, string, object as yupObject } from 'yup';

export type SourceWorkspaceData = {
  id: string;
  name: string;
  type: string;
  activitiesCount: number;
  assetsCount: number;
  routinesCount: number;
  peopleCount: number;
  alerts: number;
  groupsCount: number;
};

type DuplicateWorkspaceFormProps = {
  sourceWorkspace: SourceWorkspaceData;
  onSubmit: (data: any) => void;
};

export const DuplicateWorkspaceForm = ({
  sourceWorkspace,
  onSubmit,
}: DuplicateWorkspaceFormProps) => {
  const { t } = useTranslation();

  const formSchema = yupObject({
    name: string().required(t('workspace.create.validation.name_required')),
    type: string()
      .oneOf(Object.values(WorkspaceType))
      .required('Workspace type required'),
    ownerFirstName: string().required(
      t('workspace.create.validation.admin_first_name_required')
    ),
    ownerLastName: string().required(
      t('workspace.create.validation.admin_last_name_required')
    ),
    ownerEmail: string().email().optional().nullable().lowercase(),
    ownerPhone: PhoneValidator.optional().nullable(),
    copyActivities: boolean().required(),
    copyAssets: boolean().required(),
    copyRoutines: boolean().required(),
    copyPeople: boolean().required(),
    copyAlerts: boolean().required(),
    copyGroups: boolean().required(),
  }).test(
    'email or phone',
    (value) => !!(value.ownerPhone || value.ownerEmail)
  );

  const { control, handleSubmit, setValue, formState } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      name: `Copy of ${sourceWorkspace.name}`,
      type: sourceWorkspace.type as WorkspaceType,
      ownerFirstName: '',
      ownerLastName: '',
      ownerEmail: '',
      ownerPhone: null,
      copyActivities: true,
      copyAssets: true,
      copyRoutines: true,
      copyPeople: true,
      copyAlerts: true,
      copyGroups: true,
    },
  });

  const copyAssets = useWatch({ control, name: 'copyAssets' });
  const copyRoutines = useWatch({ control, name: 'copyRoutines' });
  const copyPeople = useWatch({ control, name: 'copyPeople' });

  const alertsEnabled = !!copyRoutines && !!copyAssets;
  const groupsEnabled = !!copyAssets && !!copyRoutines && !!copyPeople;

  // Auto-uncheck dependents when dependencies are unchecked
  useEffect(() => {
    if (!alertsEnabled) setValue('copyAlerts', false);
  }, [alertsEnabled, setValue]);

  useEffect(() => {
    if (!groupsEnabled) setValue('copyGroups', false);
  }, [groupsEnabled, setValue]);

  const entityCheckboxes: {
    name:
      | 'copyActivities'
      | 'copyAssets'
      | 'copyRoutines'
      | 'copyPeople'
      | 'copyAlerts'
      | 'copyGroups';
    label: string;
    count: number;
    enabled: boolean;
  }[] = [
    {
      name: 'copyActivities',
      label: t('workspace.duplicate.activities', {
        defaultValue: 'Activities',
      }),
      count: sourceWorkspace.activitiesCount ?? 0,
      enabled: true,
    },
    {
      name: 'copyAssets',
      label: t('workspace.duplicate.assets', { defaultValue: 'Assets' }),
      count: sourceWorkspace.assetsCount ?? 0,
      enabled: true,
    },
    {
      name: 'copyRoutines',
      label: t('workspace.duplicate.routines', { defaultValue: 'Routines' }),
      count: sourceWorkspace.routinesCount ?? 0,
      enabled: true,
    },
    {
      name: 'copyPeople',
      label: t('workspace.duplicate.people', { defaultValue: 'People' }),
      count: sourceWorkspace.peopleCount ?? 0,
      enabled: true,
    },
    {
      name: 'copyAlerts',
      label: t('workspace.duplicate.alerts', { defaultValue: 'Alerts' }),
      count: sourceWorkspace.alerts ?? 0,
      enabled: alertsEnabled,
    },
    {
      name: 'copyGroups',
      label: t('workspace.duplicate.groups', { defaultValue: 'Groups' }),
      count: sourceWorkspace.groupsCount ?? 0,
      enabled: groupsEnabled,
    },
  ];

  const handleFormSubmit = async (data: any) => {
    await onSubmit({
      sourceWorkspaceId: sourceWorkspace.id,
      name: data.name,
      type: data.type,
      owner: {
        first: data.ownerFirstName,
        last: data.ownerLastName,
      },
      ownerEmail: data.ownerEmail?.toLowerCase() || undefined,
      ownerPhone: data.ownerPhone || undefined,
      copyActivities: data.copyActivities,
      copyAssets: data.copyAssets,
      copyRoutines: data.copyRoutines,
      copyPeople: data.copyPeople,
      copyAlerts: data.copyAlerts,
      copyGroups: data.copyGroups,
    });
  };

  return (
    <View style={styles.container}>
      <ScrollView
        scrollEnabled
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Source workspace info header */}
        <View style={styles.sourceHeader}>
          <Text style={styles.sourceLabel}>
            {t('workspace.duplicate.sourceLabel', {
              defaultValue: 'Duplicating from',
            })}
          </Text>
          <Text style={styles.sourceName}>{sourceWorkspace.name}</Text>
        </View>

        {/* Workspace name */}
        <View style={styles.formItem}>
          <Text style={styles.fieldLabel}>
            {t('workspace.create.workspaceName')}
          </Text>
          <ControlledTextInput
            control={control}
            name="name"
            autoFocus={true}
            placeholder={t('workspace.create.workspaceName')}
            backgroundColor={Colors.role.inputBackground}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        {/* Workspace type */}
        <View style={[styles.formItem, { zIndex: 1000 }]}>
          <Text style={styles.fieldLabel}>{t('workspace.create.type')}</Text>
          <ControlledSelect
            control={control}
            name="type"
            label={''}
            items={Object.entries(WorkspaceType).map(([, value]) => ({
              label: t(`workspace.types.${value}`),
              value: value,
            }))}
          />
        </View>

        {/* Admin fields */}
        <View style={styles.formItem}>
          <Text style={styles.fieldLabel}>
            {t('workspace.create.administratorFirstName')}
          </Text>
          <ControlledTextInput
            control={control}
            name="ownerFirstName"
            backgroundColor={Colors.role.inputBackground}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <Text style={styles.fieldLabel}>
            {t('workspace.create.administratorLastName')}
          </Text>
          <ControlledTextInput
            control={control}
            name="ownerLastName"
            backgroundColor={Colors.role.inputBackground}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <Text style={styles.fieldLabel}>
            {t('workspace.create.administratorEmail')}
          </Text>
          <ControlledTextInput
            control={control}
            name="ownerEmail"
            isLowerCase
            backgroundColor={Colors.role.inputBackground}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <Text style={styles.fieldLabel}>
            {t('workspace.create.administratorPhone')}
          </Text>
          <ControlledPhoneInput
            control={control}
            name="ownerPhone"
            placeholder={'phone'}
            backgroundColor={Colors.role.inputBackground}
          />
        </View>

        {/* Content to copy section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {t('workspace.duplicate.contentToCopy', {
              defaultValue: 'Content to copy',
            })}
          </Text>
          {entityCheckboxes.map((entity) => (
            <EntityCheckboxRow
              key={entity.name}
              control={control}
              name={entity.name}
              label={entity.label}
              count={entity.count}
              enabled={entity.enabled}
              setValue={setValue}
            />
          ))}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[
            styles.footerButton,
            (!formState.isValid || formState.isSubmitting) &&
              styles.footerButtonDisabled,
          ]}
          onPress={
            formState.isValid && !formState.isSubmitting
              ? handleSubmit(handleFormSubmit)
              : undefined
          }
          disabled={!formState.isValid || formState.isSubmitting}
        >
          <Text style={styles.footerButtonText}>
            {formState.isSubmitting
              ? t('workspace.duplicate.duplicating', {
                  defaultValue: 'Duplicating...',
                })
              : t('workspace.duplicate.duplicateButton', {
                  defaultValue: 'Duplicate Workspace',
                })}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

type EntityCheckboxRowProps = {
  control: any;
  name: string;
  label: string;
  count: number;
  enabled: boolean;
  setValue: any;
};

const EntityCheckboxRow = ({
  control,
  name,
  label,
  count,
  enabled,
  setValue,
}: EntityCheckboxRowProps) => {
  const value = useWatch({ control, name });

  return (
    <TouchableOpacity
      style={[styles.checkboxRow, !enabled && styles.checkboxRowDisabled]}
      onPress={() =>
        enabled && setValue(name, !value, { shouldValidate: true })
      }
      disabled={!enabled}
    >
      <Checkbox
        status={value ? 'checked' : 'unchecked'}
        disabled={!enabled}
        onPress={() =>
          enabled && setValue(name, !value, { shouldValidate: true })
        }
      />
      <Text
        style={[styles.checkboxLabel, !enabled && styles.checkboxLabelDisabled]}
      >
        {label}
      </Text>
      <Text
        style={[styles.checkboxCount, !enabled && styles.checkboxLabelDisabled]}
      >
        ({count})
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingBottom: 120,
  },
  sourceHeader: {
    paddingVertical: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderControl,
    marginBottom: tokens.spacing.sm,
  },
  sourceLabel: {
    ...tokens.typography.body.small,
    color: Colors.role.textPrimary,
  },
  sourceName: {
    ...tokens.typography.label.medium,
    marginTop: 4,
    color: Colors.role.textStrong,
  },
  formItem: {
    paddingVertical: tokens.spacing.md,
    flexDirection: 'column',
    width: '100%',
  },
  fieldLabel: {
    ...tokens.typography.body.small,
    fontWeight: '600',
    marginBottom: 8,
    color: Colors.role.textStrong,
  },
  section: {
    paddingVertical: tokens.spacing.lg,
  },
  sectionTitle: {
    ...tokens.typography.label.medium,
    marginBottom: tokens.spacing.md,
    color: Colors.role.textStrong,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.sm,
  },
  checkboxRowDisabled: {
    opacity: 0.5,
  },
  checkboxLabel: {
    ...tokens.typography.body.medium,
    flex: 1,
    color: Colors.role.textStrong,
  },
  checkboxLabelDisabled: {
    color: Colors.role.textDisabled,
  },
  checkboxCount: {
    ...tokens.typography.body.small,
    color: Colors.role.textPrimary,
    marginRight: tokens.spacing.md,
  },
  footer: {
    position: 'absolute',
    left: tokens.spacing.xl,
    right: tokens.spacing.xl,
    bottom: 0,
    paddingVertical: tokens.spacing.xl,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  footerButton: {
    backgroundColor: Colors.role.surfaceInverse,
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerButtonDisabled: {
    backgroundColor: Colors.role.fillDisabled,
  },
  footerButtonText: {
    color: Colors.role.textOnInverse,
    fontWeight: tokens.typography.body.mediumEmphasised.fontWeight,
    fontSize: 16,
  },
});
